/**
 * Package D — TrainingInvitationList (trng027) same-client sync classifications.
 *
 * Flat row identity = TrainingInvitation.id
 * Audit scope:
 *   TRNG027_CREATED_AUDIT_SCOPE = training_invitations.created_at + created_by_user
 *   TRNG027_UPDATED_AUDIT_SCOPE = training_invitations.updated_at + updated_by_user
 *
 * Canonical table-complete row: GET /api/training_invitations/{id}
 * (includes list relations + audit users after Package D backend).
 */

export const TRAINING_INVITATION_LIST_UPDATED_EVENT = 'training_invitation_list:updated';
export const TRAINING_INVITATION_LIST_REMOVED_EVENT = 'training_invitation_list:removed';

export const SESSION_SAFE_UPDATE = 'SESSION_SAFE_UPDATE';
export const SESSION_INVALIDATING = 'SESSION_INVALIDATING';

export const TRNG027_CREATED_AUDIT_SCOPE = 'training_invitations.created_at+created_by_user';
export const TRNG027_UPDATED_AUDIT_SCOPE = 'training_invitations.updated_at+updated_by_user';

export const SAFE_CANONICAL_ROW_ENDPOINT = '/api/training_invitations/{id}';

const MEMBERSHIP_OR_ORDER_FIELDS = new Set([
  'name',
  'invitation_date',
  'invitation_time',
  'training_id',
  'company_id',
  'company_contact_id',
  'machines',
  'employees',
  'customs_state',
  'training',
  'company',
  'company_contact',
]);

export function isTableCompleteTrainingInvitationRow(row) {
  if (!row || typeof row !== 'object' || Array.isArray(row)) return false;
  if (row.id == null || row.id === '') return false;
  // List shape always has training relation key when loaded via show/paged.
  return Object.prototype.hasOwnProperty.call(row, 'training')
    || Object.prototype.hasOwnProperty.call(row, 'training_id');
}

/**
 * @returns {typeof SESSION_SAFE_UPDATE | typeof SESSION_INVALIDATING}
 */
export function classifyTrainingInvitationListMutation(event) {
  const detail = event?.detail;
  if (!detail || typeof detail !== 'object') return SESSION_INVALIDATING;
  if (detail.operation === 'create' || detail.operation === 'delete') return SESSION_INVALIDATING;
  if (detail.operation !== 'update') return SESSION_INVALIDATING;
  if (!isTableCompleteTrainingInvitationRow(detail.row)) return SESSION_INVALIDATING;
  const changed = Array.isArray(detail.changedFields) ? detail.changedFields : [];
  if (changed.length === 0) return SESSION_INVALIDATING;
  const risky = changed.some((f) => MEMBERSHIP_OR_ORDER_FIELDS.has(f));
  if (risky) return SESSION_INVALIDATING;
  // Pure audit / location / content-ish updates may safely patch in place.
  return SESSION_SAFE_UPDATE;
}

export function applyPagedMutationOverlay(response, overlay) {
  if (!response || typeof response !== 'object' || !Array.isArray(response.data)) return response;
  if (!overlay || typeof overlay.get !== 'function' || overlay.size === 0) return response;
  return {
    ...response,
    data: response.data.map((row) => {
      if (!row || row.id == null) return row;
      return overlay.get(String(row.id)) || row;
    }),
  };
}

export function dispatchTrainingInvitationListUpdated(detail) {
  if (typeof window === 'undefined' || typeof window.dispatchEvent !== 'function') return;
  window.dispatchEvent(new CustomEvent(TRAINING_INVITATION_LIST_UPDATED_EVENT, { detail }));
}

export function dispatchTrainingInvitationListRemoved(detail) {
  if (typeof window === 'undefined' || typeof window.dispatchEvent !== 'function') return;
  window.dispatchEvent(new CustomEvent(TRAINING_INVITATION_LIST_REMOVED_EVENT, { detail }));
}
